import { Injectable, ComponentFactoryResolver, ApplicationRef, Injector, EmbeddedViewRef, ComponentRef, inject } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { catchError, Observable, Subscription, switchMap, take, tap, throwError } from 'rxjs';
import { VersionToggleService } from './version-toggle';
import { ErrorDailogBoxComponent } from '../shared/components/error-dailog-box/error-dailog-box.component';
import { ErrorDetail, ErrorResponse, RequestBody } from '../shared/models/request-body.interface';
import { ErrorType } from '../models/error.model';
import { AuthUiService } from './auth-ui.service';
import { ErrorService } from './error.service';
import { AppFacadeService } from './app-facade.service';
import { AppMediatorService } from './app-mediator.service';
import { TranslateService } from '@ngx-translate/core';
import { ApiResultMessage, ApiResultMessageType } from '@por/shared/core';

@Injectable()
export class BackendInterceptor implements HttpInterceptor {
    private readonly versionToggle = inject(VersionToggleService);
    private readonly componentFactoryResolver = inject(ComponentFactoryResolver);
    private readonly appRef = inject(ApplicationRef);
    private readonly injector = inject(Injector);
    private readonly errorService = inject(ErrorService);
    private readonly auth = inject(AuthUiService);
    private readonly appfacade = inject(AppFacadeService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly translateService = inject(TranslateService);

    private currentDialogRef: ComponentRef<ErrorDailogBoxComponent> | null = null;

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return this.appfacade.getIsAdmin().pipe(
            take(1), // Get the latest value and complete
            switchMap(isAdmin => {
                return next.handle(request).pipe(
                    tap(event => {
                        if (event instanceof HttpResponse) {
                            const bodyMessage: ApiResultMessage = event.body?.Message ? event.body.Message : null;
                            if (bodyMessage && bodyMessage.Type === ApiResultMessageType.Error) {
                                this.handleError(
                                    {
                                        status: bodyMessage.Status ? bodyMessage.Status : event.status,
                                        url: event.url || '',
                                        error: {
                                            message: bodyMessage.Message ? bodyMessage.Message : event.statusText
                                        }
                                    },
                                    request,
                                    isAdmin
                                );
                            }
                        }
                    }),
                    catchError((error: HttpErrorResponse) => {
                        this.handleError(
                            {
                                status: error?.status ?? error?.error?.statusCode ?? 0,
                                url: request?.url || '',
                                error: {
                                    message: error?.error?.message ?? this.translateService.instant('DefaultErrorMessage')
                                }
                            },
                            request,
                            isAdmin
                        );
                        this.appfacade.setLoading(false);
                        return throwError(() => error);
                    })
                );
            })
        );
    }

    private createDialogConfig(body: RequestBody, errorStatus: string): ErrorDetail {
        return {
            errormessage: 'An error occurred while processing your request',
            quickLink: this.versionToggle.isQuickLinkVersion() ? window.location.href : '',
            orgId: body?.OrganizationId || this.appMediatorService.localStorageService.getCurrentUser?.organizationId || '',
            customerId: body?.CustomerId || this.appMediatorService.localStorageService.getCurrentUser?.customerId || '',
            date: new Date().toString(),
            contractId: body?.ContractId || this.appMediatorService.localStorageService.getCurrentUser?.contractId || '',
            errorStatus: errorStatus || ''
        };
    }

    private showDialog(config: ErrorDetail, allowClose = false): void {
        if (this.currentDialogRef) {
            this.cleanupDialog(this.currentDialogRef);
            this.currentDialogRef = null;
        }

        // Create the component manually
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(ErrorDailogBoxComponent);
        const componentRef = componentFactory.create(this.injector);
        this.currentDialogRef = componentRef;

        componentRef.instance.data = config;
        componentRef.instance.allowClose = allowClose;

        // Use object destructuring to set properties
        Object.assign(componentRef.instance, config);

        // Attach the component to the application
        this.appRef.attachView(componentRef.hostView);

        // Append the component to the body
        const domElem = (componentRef.hostView as EmbeddedViewRef<ErrorDailogBoxComponent>).rootNodes[0] as HTMLElement;
        document.body.appendChild(domElem);

        // Subscribe to retry action
        const retrySubscription = componentRef.instance.retry.subscribe(() => {
            window.location.reload(); // Reload the window on Retry
        });

        // Subscribe to closeDialog action
        const closeSubscription = componentRef.instance.closeDialog.subscribe(() => {
            this.cleanupDialog(componentRef, retrySubscription, closeSubscription);
            this.currentDialogRef = null;
        });

        // Ensure all subscriptions are unsubscribed when dialog is destroyed
        const cleanupCallback = () => {
            this.cleanupDialog(componentRef, retrySubscription, closeSubscription);
            this.currentDialogRef = null;
        };

        retrySubscription.add(cleanupCallback);
        closeSubscription.add(cleanupCallback);
    }

    private cleanupDialog(componentRef: ComponentRef<ErrorDailogBoxComponent>, ...subscriptions: Subscription[]): void {
        // Detach and destroy the component
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();

        // Unsubscribe to prevent memory leaks
        subscriptions.forEach(sub => sub.unsubscribe());
    }

    private handleError(error: ErrorResponse, request: HttpRequest<unknown>, isAdmin: boolean) {
        let dialogConfig: ErrorDetail = this.createDialogConfig(request.body as RequestBody, error.status.toString());
        /**
         * if online, but error.status === 0, mean GAPI is timeout, consider it as 502
         */
        if (navigator.onLine && error.status === 0) {
            error = {
                ...error,
                status: 502
            };
        }
        const errorMessage = this.errorHandlingAlerts(error.status, error.error.message);
        dialogConfig = {
            ...dialogConfig,
            errorStatus: error.status.toString(),
            errormessage: errorMessage,
            apiMessage: error.error.message
        };
        const isNetworkError = error.status === 0;
        const isOffline = !navigator.onLine;

        /**
         * First check for Offline, then check for api connectivity.
         */
        if (isOffline) {
            dialogConfig = {
                ...dialogConfig,
                errormessage: this.translateService.instant('ApplicationOffline')
            };
            this.showDialog(dialogConfig);
        } else if (isNetworkError) {
            dialogConfig = {
                ...dialogConfig,
                errormessage: this.translateService.instant('NetworkError')
            };
            this.showDialog(dialogConfig);
        }
        const isInternalError = [500, 404].includes(error.status);
        const isBadRequestError = [400].includes(error.status);
        const isRefreshTokenError = error?.url?.includes('tokens/refresh');
        const isGoogleMapError = error?.url?.includes('maps.googleapis.com');

        /**
         * Check for SQS message, because when There is no expert server tied to your organization
         * GAPI returns message with 403 status code
         */
        const isSQSError = [502, 504].includes(error.status) || error.error.message.includes('SQS');
        const excludeUrls: string[] = ['getpdf', 'REST/v1/Locations', 'create-transaction', 'livelink', 'sms/send'];

        if (isAdmin) {
            excludeUrls.push('public/organizations/url');
            excludeUrls.push('external-credentials/hapn/test');
        }
        const isExcludedUrl: boolean = excludeUrls.some(url => request.url.includes(url));

        if (isGoogleMapError) {
            return;
        }

        const isAuthError = [401, 403].includes(error.status);

        // Handle 401 Unauthorized & 403 Forbidden - logout or reload
        if ((isAuthError || (!error?.status && isRefreshTokenError)) && !isSQSError && !isExcludedUrl && !this.versionToggle.isQuickLinkVersion()) {
            this.errorService.setError(ErrorType.AuthFailed);
            this.auth.logout();
        } else if (isAuthError && this.versionToggle.isQuickLinkVersion()) {
            window.location.reload();
        }
        // Handle SQS errors (502/504 or SQS in message)
        else if (isSQSError) {
            dialogConfig = {
                ...dialogConfig,
                errormessage: this.translateService.instant('SQSIssue'),
                apiMessage: errorMessage
            };
            this.showDialog(dialogConfig, this.versionToggle.isQuickLinkVersion() ? false : true);
        }
        // Handle 400 Bad Request - show dialog with close button
        else if (isBadRequestError && !isExcludedUrl) {
            dialogConfig = {
                ...dialogConfig,
                apiMessage: error.error.message || this.translateService.instant('BadRequest')
            };
            this.showDialog(dialogConfig, true);
        }
        // Handle other errors (404, 500, etc.) - show dialog
        else if (isInternalError && !isExcludedUrl) {
            this.showDialog(dialogConfig);
        }
    }

    private errorHandlingAlerts(status: number, apiMessage: string) {
        let message = '';
        switch (status) {
            case 400:
            case 401:
            case 403:
            case 404:
                message = apiMessage;
                break;
            case 502:
                message = this.translateService.instant('ApiStatus502');
                break;
            case 504:
                message = this.translateService.instant('ApiStatus504');
                break;
            default:
                message = this.translateService.instant('DefaultErrorMessage');
                break;
        }

        return message;
    }
}
